import React, { useContext, useMemo } from 'react';

import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext.ts';
import type { RendererExtensionProviders } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext.ts';
import { ClassicEditorContext } from '@confluence/editor-features/entry-points/ClassicEditorContext.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { PageIncludeMacroHandler } from '@confluence/page-include-macro/entry-points/PageIncludeMacroRenderer.ts';
import type {
	PageIncludeMacroParams,
	PageIncludeRendererWrapperProps,
} from '@confluence/page-include-macro/entry-points/PageIncludeMacroTypes.ts';

type PageIncludeExtensionHandlerProps = ExtensionHandlerProps & {
	Renderer: React.FC<any>;
	appearance?: string;
	macroParams?: PageIncludeMacroParams;
	rendererExtensionOptions?: RendererExtensionProviders;
};

type PageIncludeRendererWrapperComponentProps = PageIncludeRendererWrapperProps & {
	Renderer: React.FC<any>;
	appearance?: string;
	rendererExtensionOptions: RendererExtensionProviders;
};

const PageIncludeRendererWrapper = ({
	Renderer,
	appearance,
	rendererExtensionOptions,
	adf,
	targetContentId,
	targetSpaceKey,
	mediaToken,
}: PageIncludeRendererWrapperComponentProps) => {
	const rendererAppearance = appearance === 'full-page' ? 'full-page' : 'full-width';
	const rendererData = useMemo(
		() =>
			({
				content: {
					nodes: [
						{
							body: {
								dynamic: {
									representation: 'atlas_doc_format',
									value: JSON.stringify({ content: adf, type: 'doc' }),
								},
							},
						},
					],
				},
			}) as any,
		[adf],
	);

	return (
		<RendererExtensionContext.Provider value={rendererExtensionOptions}>
			<Renderer
				contentId={targetContentId}
				spaceKey={targetSpaceKey}
				data={rendererData}
				appearance={rendererAppearance}
				hasInlineComments={false}
				mediaToken={mediaToken}
			/>
		</RendererExtensionContext.Provider>
	);
};

export const PageIncludeExtensionHandler = ({
	Renderer,
	appearance,
	macroParams,
	rendererExtensionOptions,
	...macroDefaultProps
}: PageIncludeExtensionHandlerProps) => {
	const { getExtensionHandlers, populateExtensionProviders } = useContext(ClassicEditorContext);

	const contextRendererExtensionOptions = useMemo(
		() => ({
			getExtensionHandlers,
			populateExtensionProviders,
		}),
		[getExtensionHandlers, populateExtensionProviders],
	);

	const resolvedRendererExtensionOptions =
		rendererExtensionOptions ?? contextRendererExtensionOptions;

	const RendererWrapper = useMemo(
		() => (props: PageIncludeRendererWrapperProps) => (
			<PageIncludeRendererWrapper
				{...props}
				Renderer={Renderer}
				appearance={appearance}
				rendererExtensionOptions={resolvedRendererExtensionOptions}
			/>
		),
		[appearance, Renderer, resolvedRendererExtensionOptions],
	);

	return (
		<PageIncludeMacroHandler
			macroDefaultProps={macroDefaultProps}
			macroParams={macroParams}
			RendererWrapper={RendererWrapper}
		/>
	);
};
